import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { ServerIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

const SMTPEmailNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected }) => {
  const smtpConfig = data.config?.smtp || {};
  const fromConfig = data.config?.from || {};
  const recipientMode = data.config?.recipientMode || 'single';
  const customerGroupId = data.config?.customerGroupId || '';
  
  const isBulkMode = recipientMode === 'group' && customerGroupId;
  
  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-orange-400 to-orange-500 border-2 transition-all duration-200 ${
      selected ? 'border-orange-600 shadow-xl scale-105' : 'border-orange-300'
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <ServerIcon className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className="text-xs text-orange-100 truncate mt-1">
              {data.description}
            </div>
          )}
          
          {/* SMTP type indicator */}
          <div className="flex items-center mt-1 space-x-1">
            <span className="text-sm">🔧</span>
            <span className="text-xs text-orange-100">SMTP Email</span>
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-700' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Configuration preview */}
      <div className="mt-2 space-y-1">
        {/* SMTP Server info */}
        {smtpConfig.host && (
          <div className="text-xs text-orange-100 bg-black bg-opacity-20 px-2 py-1 rounded">
            <strong>SMTP:</strong>
            <span className="ml-1 truncate">
              {smtpConfig.host}:{smtpConfig.port || 587}
              {smtpConfig.secure && ' (SSL)'}
            </span>
          </div>
        )}
        
        {/* From info */}
        {(fromConfig.name || fromConfig.email) && (
          <div className="text-xs text-orange-100 bg-black bg-opacity-20 px-2 py-1 rounded">
            <strong>Feladó:</strong>
            <span className="ml-1 truncate">
              {fromConfig.name && fromConfig.email 
                ? `${fromConfig.name} <${fromConfig.email}>`
                : fromConfig.name || fromConfig.email || 'Nincs beállítva'}
            </span>
          </div>
        )}
        
        {/* Recipient */}
        {isBulkMode ? (
          <div className="text-xs text-orange-100 bg-black bg-opacity-20 px-2 py-1 rounded">
            <strong>👥 Tömeges küldés:</strong>
            <span className="ml-1 truncate">Ügyfélcsoport</span>
          </div>
        ) : data.config?.recipient ? (
          <div className="text-xs text-orange-100 bg-black bg-opacity-20 px-2 py-1 rounded">
            <strong>Címzett:</strong>
            <span className="ml-1 truncate">{data.config.recipient}</span>
          </div>
        ) : null}
        
        {/* Subject */}
        {data.config?.subject && (
          <div className="text-xs text-orange-100 bg-black bg-opacity-20 px-2 py-1 rounded">
            <strong>Tárgy:</strong>
            <span className="ml-1 truncate">{data.config.subject}</span>
          </div>
        )}
        
        {/* Body preview */}
        {data.config?.body && (
          <div className="text-xs text-orange-100 bg-black bg-opacity-20 px-2 py-1 rounded">
            <strong>Üzenet:</strong>
            <div className="truncate mt-1 opacity-90">
              {data.config.body.length > 50 
                ? `${data.config.body.substring(0, 50)}...` 
                : data.config.body}
            </div>
          </div>
        )}
        
        {/* SMTP User info */}
        {smtpConfig.user && (
          <div className="text-xs text-orange-100 bg-black bg-opacity-20 px-2 py-1 rounded">
            <strong>Felhasználó:</strong>
            <span className="ml-1 truncate">{smtpConfig.user}</span>
          </div>
        )}
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-orange-200 bg-red-700 bg-opacity-30 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Execution status */}
      {data.executionData && (
        <div className="mt-2 text-xs text-orange-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              data.executionData.success ? 'bg-green-400' : 'bg-red-400'
            }`} />
            <span>
              {data.executionData.success ? 'SMTP Email elküldve' : 'SMTP küldés sikertelen'}
            </span>
          </div>
          {data.executionData.messageId && (
            <div className="font-mono text-xs mt-1">
              ID: {data.executionData.messageId}
            </div>
          )}
          {data.executionData.smtp_host && (
            <div className="text-xs mt-1 opacity-75">
              via {data.executionData.smtp_host}
            </div>
          )}
        </div>
      )}

      {/* Connection status indicator */}
      {smtpConfig.host && smtpConfig.user && (
        <div className="mt-2 flex items-center space-x-1">
          <EnvelopeIcon className="w-3 h-3 text-orange-200" />
          <span className="text-xs text-orange-200">
            {smtpConfig.pass ? 'Konfigurálva' : 'Jelszó hiányzik'}
          </span>
        </div>
      )}

      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-orange-600 border-2 border-white"
      />

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-orange-600 border-2 border-white"
      />
    </div>
  );
};

export default SMTPEmailNode;
